import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';

type Props = {
  deviceId: string;
};

const CallMonitoringPanel: React.FC<Props> = ({ deviceId }) => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.call_monitoring.read')
    || permissions.includes('call_qa.configure')
    || permissions.includes('monitoring.diagnostics.read')
    || user?.role === 'super_user'
    || user?.role === 'administrator';
  const canToggle = permissions.includes('call_qa.call_monitoring.enable_device')
    || permissions.includes('call_qa.configure')
    || user?.role === 'super_user'
    || user?.role === 'administrator';
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['callMonitoringDevice', deviceId],
    queryFn: () => apiClient.monitoring.getDeviceCallMonitoring(deviceId),
    enabled: canRead && !!deviceId,
    refetchInterval: 15000,
  });

  const { data: diagnosticsData } = useQuery({
    queryKey: ['deviceDiagnostics', deviceId],
    queryFn: () => apiClient.monitoring.getDeviceDiagnostics(deviceId),
    enabled: canRead && !!deviceId,
    refetchInterval: 20000,
  });

  const { data: recentCallsData } = useQuery({
    queryKey: ['callMonitoringRecentCalls', deviceId],
    queryFn: () => apiClient.callQa.listCalls({
      device_id: deviceId,
      limit: 10,
      recording_source: 'DESKTOP_CAPTURE',
    }),
    enabled: canRead && !!deviceId,
    refetchInterval: 30000,
  });

  const status = data?.data?.data;
  const cmDiag = diagnosticsData?.data?.data?.call_monitoring;
  const deviceEnabled = status?.device_enabled === true
    || status?.call_monitoring_device_enabled === true;
  const globalEnabled = status?.global_enabled === true
    || status?.call_monitoring_global_enabled === true;
  const effective = status?.effective_enabled === true
    || status?.call_monitoring_effective === true;
  const currentState = cmDiag?.current_state
    || status?.current_state
    || (effective ? 'Idle' : 'Disabled');
  const recentCalls = recentCallsData?.data?.data?.rows
    || recentCallsData?.data?.data?.items
    || recentCallsData?.data?.data?.calls
    || [];

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.monitoring.updateDeviceCallMonitoring(deviceId, {
        enabled,
        reason: enabled ? 'Enabled from Computer Details' : 'Disabled from Computer Details',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callMonitoringDevice', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['deviceDiagnostics', deviceId] });
    },
  });

  if (!canRead) {
    return (
      <Alert severity="warning">Call Monitoring requires call_qa.call_monitoring.read permission.</Alert>
    );
  }

  if (isLoading) {
    return <Typography variant="body2">Loading Call Monitoring…</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load Call Monitoring status.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>CALL MONITORING</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Device-centric automatic call capture. Independent of TEKS sign-in and Clock In.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              size="small"
              color={deviceEnabled ? 'success' : 'default'}
              label={deviceEnabled ? 'Call Monitoring: ON' : 'Call Monitoring: OFF'}
            />
            <Chip size="small" label={`Global: ${globalEnabled ? 'ON' : 'OFF'}`} />
            <Chip
              size="small"
              color={effective ? 'success' : 'warning'}
              label={`Effective: ${effective ? 'Enabled' : 'Disabled'}`}
            />
            <Chip
              size="small"
              label={`Capabilities: ${status?.supported === false ? 'Unsupported' : 'Supported'}`}
            />
            <Chip size="small" color="info" label={`State: ${currentState}`} />
          </Stack>
          {!globalEnabled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Global Call Monitoring is OFF. Enable it in Call QA → Configuration to activate capture on this computer.
            </Alert>
          )}
          {globalEnabled && deviceEnabled
            && String(status?.call_recording_app_mode || status?.callRecordingAppMode || 'ALL').toUpperCase() === 'ALLOWLIST'
            && !(status?.softphone_process_allowlist?.length > 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Softphone process allowlist is empty while app mode is ALLOWLIST. Add process names in Call QA → Configuration, or switch app mode to ALL.
            </Alert>
          )}
          {canToggle && (
            <Box>
              <Button
                variant="contained"
                color={deviceEnabled ? 'inherit' : 'primary'}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate(!deviceEnabled)}
              >
                {deviceEnabled ? 'Disable Call Monitoring' : 'Enable Call Monitoring'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Diagnostics</Typography>
          {cmDiag?.implementation_status && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Implementation: {cmDiag.implementation_status}
              {cmDiag.production_ready === false ? ' — not production-ready' : ''}
              {cmDiag.detection_method ? ` · Detection: ${cmDiag.detection_method}` : ''}
              {cmDiag.audio_capture_mode ? ` · Audio: ${cmDiag.audio_capture_mode}` : ''}
            </Alert>
          )}
          <Typography variant="body2">Supported: {cmDiag?.supported === false ? 'No' : 'Yes'}</Typography>
          <Typography variant="body2">Detection Engine: {cmDiag?.detection_engine || '—'}</Typography>
          <Typography variant="body2">Audio Capture: {cmDiag?.audio_capture || '—'}</Typography>
          <Typography variant="body2">Configured Softphone: {cmDiag?.configured_softphone || status?.softphone_process_allowlist?.join(', ') || 'None'}</Typography>
          <Typography variant="body2">Queue Pending: {cmDiag?.queue_pending ?? '—'}</Typography>
          <Typography variant="body2">Queue Failed: {cmDiag?.queue_failed ?? '—'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Runtime agent fields update when Companion reports Call Monitoring health.
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Recent desktop calls</Typography>
          {Array.isArray(recentCalls) && recentCalls.length > 0 ? (
            <Stack spacing={1}>
              {recentCalls.slice(0, 8).map((call: any) => (
                <Box key={call.id || call.call_id} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    {call.started_at || call.start_time || call.created_at || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {call.duration_seconds != null ? `${call.duration_seconds}s` : (call.duration || '—')}
                  </Typography>
                  <Typography variant="body2">
                    {call.employee_name || call.employeeName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">{call.recording_status || call.status || '—'}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No desktop-captured calls for this computer yet.
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => window.location.assign('/call-qa/calls')}
          >
            Open Call QA
          </Button>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default CallMonitoringPanel;
